import React from 'react';
import { ContentType } from '@components/learning/types/index';
import { Heading, Tag, Text } from '@components/shared';
import { ProgressBar } from '@components/learning/ProgressBar';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { LearningControlPanel } from '@components/learning/LearningControlPanel';

export enum InformationCardState {
  LOADING = 'loading',
  DEFAULT = 'default',
  IN_PROGRESS = 'in_progress',
  FINISHED = 'finished',
}

export interface InformationCardProps {
  /** Type of content (Course or Learning Path) */
  contentType: ContentType;
  /** Optional video element to display in the Learning path card */
  video?: React.ReactElement;
  /** Optional badge image URL */
  badge?: string;
  /** Progress percentage (0-100) */
  progress: number;
  /** Duration text displayed in the Tag */
  duration: string;
  /** Description text content */
  description: React.ReactNode;
  /** Optional custom title text. If not provided, default title will be used based on contentType */
  title?: string;
  /** Defines the visual state of the card to show or not different buttons */
  cardState: InformationCardState;
  /** Indicates when star icon should be filled */
  isSaved: boolean;
  /** Function to be executed on save button */
  onSave?: (() => void) | null;
  /** Url of the resource to be shared */
  urlToShare: string;
  /** Function to be executed on click button */
  onClick: () => void;
}

/**
 * InformationCard displays a card with information about a course or learning path.
 * @param {InformationCardProps} InformationCardProps
 * @returns {React.ReactElement} React.ReactElement
 *
 * @example
 * <InformationCard contentType={InformationCardState.DEFAULT} />
 */
export const InformationCard: React.FC<InformationCardProps> = ({
  contentType,
  video,
  badge,
  progress,
  duration,
  description,
  title,
  urlToShare,
  cardState,
  isSaved,
  onSave,
  onClick,
}: InformationCardProps): React.ReactElement => {
  const isCourse = contentType === ContentType.COURSE;
  const defaultTitle = isCourse ? "After this course, you'll be able to:" : 'This learning path is for:';
  const displayTitle = title ?? defaultTitle;
  const titleColor = isCourse ? token('color.text.accent.blue') : token('color.text.accent.lime');
  const isCompleted = progress === 100;
  const shouldShowBadge = isCourse && badge && !video;
  const shouldShowVideo = contentType === ContentType.LEARNING_PATH && video && !badge;

  return (
    <div
      data-testid="information-card"
      css={[
        informationCardStyles,
        isCourse ? courseBorderStyles : learningPathBorderStyles,
        shouldShowVideo &&
          css({
            borderTop: 'none',
          }),
      ]}
    >
      {shouldShowBadge && (
        <div css={badgeContainerStyles}>
          <div
            css={badgeStyles}
            style={{
              backgroundImage: `url(${badge})`,
            }}
          />
        </div>
      )}
      {shouldShowVideo && <div css={videoContainerStyles}>{video}</div>}
      <div
        data-testid="information-card-top"
        css={[informationCardContainerStyles, css({ marginTop: shouldShowBadge ? token('space.250') : '0px' })]}
      >
        <Heading level={5} color={titleColor}>
          {displayTitle}
        </Heading>
        <div css={descriptionStyles}>
          <Text size="small" color={token('color.text')}>
            {description}
          </Text>
        </div>
      </div>
      <div
        data-testid="duration-progress-container"
        css={[informationCardContainerStyles, durationProgressContainerStyles]}
      >
        <div data-testid="duration-info" css={durationContainerStyles}>
          <span css={spanStyles}>Duration</span>
          <Tag label={duration} />
        </div>
        <div data-testid="progress-info" css={progressContainerStyles}>
          <div css={progressPercentageStyles}>
            <span css={spanStyles}>Progress </span>
            <span
              css={[
                percentageStyles,
                isCompleted &&
                  css({
                    color: token('color.text.success'),
                  }),
              ]}
            >
              {isCompleted ? 'Completed' : `${progress}%`}
            </span>
          </div>
          <ProgressBar color={token('color.border.success')} progress={progress} />
        </div>
      </div>
      <div css={controlPanelContainerStyles}>
        <LearningControlPanel
          onClick={onClick}
          isSaved={isSaved}
          onSave={onSave}
          state={cardState}
          urlToShare={urlToShare}
          contentType={contentType}
        />
      </div>
    </div>
  );
};

const informationCardStyles = css({
  position: 'relative',
  width: '100%',
  border: `1px solid ${token('color.border')}`,
  borderBottom: 'unset',
  borderRadius: '8px',
  backgroundColor: token('color.background.input'),
  boxShadow: token('elevation.shadow.overlay'),

  '@media (max-width: 965px)': {
    borderBottom: `1px solid ${token('color.border')}`,
    boxShadow: 'unset',
  },
});

const courseBorderStyles = css({
  borderTop: `8px solid ${token('color.text.accent.blue')}`,
});

const learningPathBorderStyles = css({
  borderTop: '8px solid #6A9A23',
});

const badgeContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  border: `4px solid ${token('color.text.accent.blue')}`,
  left: '50%',
  top: '0%',
  transform: 'translate(-50%, -60%)',
  background: token('color.background.input'),
  zIndex: 1,
});

const videoContainerStyles = css({
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',

  '.plyr': {
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
  },
});

const badgeStyles = css({
  width: '35px',
  height: '35px',
  borderRadius: '50%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
});

const informationCardContainerStyles = css({
  padding: token('space.200'),
});

const descriptionStyles = css({
  marginBottom: token('space.100'),
  marginTop: token('space.100'),
  color: `${token('color.text')} !important`,
  fontFamily: 'Inter !important',
  fontSize: '14px !important',
  fontWeight: '400 !important',
  lineHeight: '22px !important',

  div: {
    paddingBottom: '0 !important',
    display: 'flex !important',
    '@media (max-width: 920px)': {
      display: 'flex !important',
    },
  },

  'span, p, li': {
    color: `${token('color.text')} !important`,
    fontFamily: 'Inter !important',
    fontSize: '14px !important',
    fontWeight: '400 !important',
    lineHeight: '22px !important',
  },

  li: {
    marginBottom: '6px !important',
  },
  'li:last-child': {
    marginBottom: '0 !important',
  },
});

const durationProgressContainerStyles = css({
  borderTop: `1px solid ${token('color.border')}`,
});

const durationContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.200'),
});

const spanStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: '700',
  lineHeight: '20px',
  color: token('color.text'),
  textTransform: 'uppercase',
});

const percentageStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '20px',
  color: token('color.text'),
});

const progressContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
  marginTop: token('space.200'),
});

const progressPercentageStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.200'),
});

const controlPanelContainerStyles = css({
  '@media (max-width: 965px)': {
    display: 'none',
  },
});
